/* ============================================================================
   AKINOD — Compteurs animés
   ----------------------------------------------------------------------------
   Anime un nombre de 0 jusqu'à sa valeur cible quand il entre dans le champ de
   vision. Une seule fois : pas de va-et-vient au défilement.

   Contrat de balisage :
     <span data-ak-count="122">122</span>
   La valeur finale est écrite dans le HTML par Liquid. Le script ne fait que
   la rejouer : sans JavaScript, le chiffre est déjà juste.

   `prefers-reduced-motion` est respecté strictement — on pose la valeur finale
   immédiatement, sans aucune interpolation.

   Idempotent (marquage `data-ak-count-bound`, propre à ce comportement) : l'éditeur de thème peut réinjecter la
   section sans doubler les observateurs.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const DURATION = 1300;   // ms — durée de la montée
  const THRESHOLD = 0.6;   // le bloc doit être bien engagé avant de démarrer

  /* --- Mise en forme -------------------------------------------------------- *
   * On sépare les milliers selon la langue du document (2431 → « 2 431 » en
   * français). Si Intl manque à l'appel, on retombe sur le nombre brut.        */

  let format;
  try {
    const locale = document.documentElement.lang || 'fr-FR';
    const nf = new Intl.NumberFormat(locale);
    format = (n) => nf.format(n);
  } catch {
    format = (n) => String(n);
  }

  /* --- Courbe ---------------------------------------------------------------- *
   * Cubique sortante : départ franc, arrivée posée. C'est la même intention que
   * la courbe signature de la charte, transposée sur une valeur numérique.     */

  const easeOutCubic = (k) => 1 - Math.pow(1 - k, 3);

  /* --- Animation d'un compteur ------------------------------------------------ */

  function run(el) {
    // On tolère les espaces de milliers saisis dans l'éditeur (« 1 500 »),
    // y compris l'insécable et l'insécable étroit.
    const raw = (el.dataset.akCount || '').replace(/[\s\u00A0\u202F]/g, '');

    // Rien que des chiffres, sinon on laisse le contenu rendu par Liquid tel
    // quel : « 2CR14 », « 24/7 » ou « 1.500 » ne sont pas des nombres, et
    // `parseInt` n'en garderait que la tête — qu'il afficherait à leur place.
    if (!/^\d+$/.test(raw)) return;

    const target = Number.parseInt(raw, 10);

    if (REDUCED) {
      el.textContent = format(target);
      return;
    }

    let start = null;

    const tick = (now) => {
      if (start === null) start = now;
      const k = Math.min(1, (now - start) / DURATION);
      el.textContent = format(Math.round(target * easeOutCubic(k)));
      if (k < 1) requestAnimationFrame(tick);
    };

    el.textContent = format(0);
    requestAnimationFrame(tick);
  }

  /* --- Amorçage ---------------------------------------------------------------- */

  function init(root = document) {
    const items = root.querySelectorAll('[data-ak-count]:not([data-ak-count-bound])');
    if (!items.length) return;

    // Sans IntersectionObserver, ou en mouvement réduit, on pose la valeur finale
    // plutôt que de laisser un zéro à l'écran.
    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach((el) => {
        el.dataset.akCountBound = '1';
        run(el);
      });
      return;
    }

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        run(entry.target);
      }
    }, { threshold: THRESHOLD });

    items.forEach((el) => {
      el.dataset.akCountBound = '1';
      io.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // L'éditeur de thème réinjecte les sections sans recharger la page.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
